/* crm-ai.jsx — AI Foundation components for Phase 3
   Provides: AIAssistantPanel, AIDealSummary, AIOpportunityHealth,
             AIRecommendations, AIMeetingSummary, AIEmailDraft, AISearchBar
   All components use the AI_CONTRACT pattern: { record, entityType, onRequest, onFeedback }
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── AI badge ── */
const AIBadge = () => {
  const store = useStore?.();
  return (
    <span className="nx-ai-compact-badge">
      {(store?.branding?.brandName || 'AI') + ' AI'}
    </span>
  );
};

/* ── Skeleton line ── */
const SkeletonLine = ({ width = '100%', height = 10 }) => (
  <div style={{ width, height, background: 'var(--nx-border)', borderRadius: 4, animation: 'pulse 1.5s infinite' }} />
);

/* ── AI card wrapper ── */
const AICard = ({ icon = 'Sparkles', title, badge, children, loading, onRefresh, refreshLabel = 'Regenerate' }) => (
  <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10, overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--nx-border)', background: 'linear-gradient(135deg, #f8f7ff, #f0f4ff)' }}>
      <NexusIcon name={icon} size={13} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, fontSize: 12, flex: 1 }}>{title}</span>
      {badge && <AIBadge />}
      {onRefresh && !loading && (
        <button
          onClick={onRefresh}
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--nx-accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4 }}
        >
          <NexusIcon name="RefreshCw" size={10} /> {refreshLabel}
        </button>
      )}
    </div>
    <div style={{ padding: '12px' }}>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SkeletonLine width="90%" />
          <SkeletonLine width="75%" />
          <SkeletonLine width="55%" />
        </div>
      ) : children}
    </div>
  </div>
);

/* ── Derive mock AI output from record data ── */
const deriveDealSummary = (record) => {
  if (!record) return 'No record selected.';
  const val   = record.value ? '$' + Number(record.value).toLocaleString() : 'an unspecified value';
  const owner = record.owner || 'the assigned rep';
  const close = record.closeDate || 'no close date set';
  const prob  = record.probability ?? record.prob ?? '—';
  return `${record.title || 'This deal'} is a ${val} opportunity owned by ${owner}, targeting a close by ${close}. Current win probability is ${prob}%.`;
};

const deriveNextStep = (record) => {
  const days = record?.age || 0;
  if (days > 30) return 'This deal has been open for over 30 days. Schedule a stakeholder review meeting and confirm budget approval status.';
  if (!record?.closeDate) return 'No close date is set. Work with the prospect to agree on a target decision timeline.';
  return 'Prepare and send a tailored proposal. Follow up with the economic buyer to confirm fit and next steps.';
};

const deriveRiskFactors = (record, pipeline) => {
  const risks = [];
  const stage = (pipeline || []).find(s => s.id === record?.stageId);
  if ((record?.age || 0) > 21) risks.push({ icon: 'Clock', label: 'Deal stalling', detail: 'No stage change in 21+ days.', tone: 'red' });
  if (!record?.closeDate) risks.push({ icon: 'Calendar', label: 'No close date', detail: 'Close date not confirmed with prospect.', tone: 'amber' });
  if ((record?.probability ?? stage?.prob ?? 50) < 40) risks.push({ icon: 'TrendingDown', label: 'Low probability', detail: 'Win probability below 40%. Review qualification.', tone: 'amber' });
  if (risks.length === 0) risks.push({ icon: 'CheckCircle', label: 'No critical risks', detail: 'Deal health looks good based on available data.', tone: 'green' });
  return risks;
};

/* ══════════════════════════════════════════════════════════════
   AI DEAL SUMMARY
   ══════════════════════════════════════════════════════════════ */
const AIDealSummary = ({ record, entityType, onRequest, onFeedback }) => {
  const [loading, setLoading] = useState(false);
  const store = useStore();

  const summary = useMemo(() => deriveDealSummary(record), [record]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      if (typeof onRequest === 'function') await onRequest('Summarize this deal.');
    } catch { /* noop */ }
    setTimeout(() => setLoading(false), 1200);
  };

  return (
    <AICard icon="FileText" title="Deal Summary" badge loading={loading} onRefresh={handleRefresh}>
      <p style={{ fontSize: 12, color: 'var(--nx-text)', lineHeight: 1.6, margin: 0 }}>{summary}</p>
      {onFeedback && (
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button onClick={() => onFeedback('up')} style={{ fontSize: 10, color: 'var(--nx-text-muted)', background: 'transparent', border: '1px solid var(--nx-border)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
            <NexusIcon name="ThumbsUp" size={9} /> Helpful
          </button>
          <button onClick={() => onFeedback('down')} style={{ fontSize: 10, color: 'var(--nx-text-muted)', background: 'transparent', border: '1px solid var(--nx-border)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
            <NexusIcon name="ThumbsDown" size={9} /> Not helpful
          </button>
        </div>
      )}
    </AICard>
  );
};
window.AIDealSummary = AIDealSummary;

/* ══════════════════════════════════════════════════════════════
   AI OPPORTUNITY HEALTH
   ══════════════════════════════════════════════════════════════ */
const AIOpportunityHealth = ({ record, entityType, onRequest, onFeedback }) => {
  const store  = useStore();
  const risks  = useMemo(() => deriveRiskFactors(record, store.pipeline), [record, store.pipeline]);
  const overallHealth = risks.filter(r => r.tone === 'red').length > 0 ? 'Red'
    : risks.filter(r => r.tone === 'amber').length > 0 ? 'Amber' : 'Green';
  const healthColor = { Red: 'var(--nx-danger)', Amber: '#F59E0B', Green: 'var(--nx-success)' }[overallHealth];

  return (
    <AICard icon="Activity" title="Opportunity Health" badge>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: healthColor + '18', border: `2px solid ${healthColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NexusIcon name={overallHealth === 'Green' ? 'CheckCircle' : overallHealth === 'Amber' ? 'AlertCircle' : 'XCircle'} size={14} style={{ color: healthColor }} />
        </div>
        <span style={{ fontWeight: 700, fontSize: 14, color: healthColor }}>{overallHealth}</span>
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Overall signal</span>
      </div>
      {risks.map((r, i) => {
        const tc = { red: 'var(--nx-danger)', amber: '#F59E0B', green: 'var(--nx-success)' }[r.tone] || 'var(--nx-text-muted)';
        return (
          <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderTop: i > 0 ? '1px solid var(--nx-border)' : 'none' }}>
            <NexusIcon name={r.icon} size={12} style={{ color: tc, flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: tc }}>{r.label}</div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{r.detail}</div>
            </div>
          </div>
        );
      })}
    </AICard>
  );
};
window.AIOpportunityHealth = AIOpportunityHealth;

/* ══════════════════════════════════════════════════════════════
   AI RECOMMENDATIONS
   ══════════════════════════════════════════════════════════════ */
const AIRecommendations = ({ record, entityType, onRequest, onFeedback }) => {
  const store = useStore();
  const recs  = useMemo(() => {
    const base = [
      { icon: 'Mail',       label: 'Send a check-in email to the primary contact',         priority: 'High' },
      { icon: 'Phone',      label: 'Schedule a call to verify budget approval',             priority: 'High' },
      { icon: 'Users',      label: 'Identify additional stakeholders in the buying committee', priority: 'Medium' },
      { icon: 'FileText',   label: 'Prepare a tailored proposal or case study',             priority: 'Medium' },
      { icon: 'Calendar',   label: 'Book a product demo with the economic buyer',           priority: 'Low'  },
    ];
    if ((record?.age || 0) > 21) {
      base.unshift({ icon: 'AlertCircle', label: 'Follow up immediately — deal has been stalled for 21+ days', priority: 'High' });
    }
    return base.slice(0, 5);
  }, [record]);

  return (
    <AICard icon="Zap" title="Recommended Actions" badge>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {recs.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: r.priority === 'High' ? '#FEF2F2' : r.priority === 'Medium' ? '#EDF4FD' : 'var(--nx-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NexusIcon name={r.icon} size={11} style={{ color: r.priority === 'High' ? 'var(--nx-danger)' : r.priority === 'Medium' ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, lineHeight: 1.4 }}>{r.label}</div>
              <Pill tone={r.priority === 'High' ? 'red' : r.priority === 'Medium' ? 'blue' : 'gray'}>{r.priority}</Pill>
            </div>
          </div>
        ))}
      </div>
    </AICard>
  );
};
window.AIRecommendations = AIRecommendations;

/* ══════════════════════════════════════════════════════════════
   AI MEETING SUMMARY
   ══════════════════════════════════════════════════════════════ */
const AIMeetingSummary = ({ record, entityType, onRequest, onFeedback }) => {
  const [loading, setLoading] = useState(false);

  const bullets = useMemo(() => {
    const title = record?.title || record?.name || 'the account';
    return [
      `Discussed current pain points and strategic priorities with ${title}.`,
      'Prospect confirmed interest in a proof-of-concept; timeline TBD.',
      'Budget holder is engaged but requires board sign-off for deals above $50K.',
      'Agreed to reconvene in two weeks with a formal proposal and ROI model.',
      'Key objection: concerns around data migration complexity.',
    ];
  }, [record]);

  const handleRefresh = async () => {
    setLoading(true);
    try { if (typeof onRequest === 'function') await onRequest('Summarize the last meeting.'); } catch { /* noop */ }
    setTimeout(() => setLoading(false), 1200);
  };

  return (
    <AICard icon="Calendar" title="Meeting Summary" badge loading={loading} onRefresh={handleRefresh} refreshLabel="Re-generate">
      <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 8 }}>AI-generated from latest meeting notes</div>
      <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {bullets.map((b, i) => (
          <li key={i} style={{ fontSize: 12, color: 'var(--nx-text)', lineHeight: 1.5 }}>{b}</li>
        ))}
      </ul>
    </AICard>
  );
};
window.AIMeetingSummary = AIMeetingSummary;

/* ══════════════════════════════════════════════════════════════
   AI EMAIL DRAFT
   ══════════════════════════════════════════════════════════════ */
const AIEmailDraft = ({ record, entityType, onRequest, onFeedback }) => {
  const [loading,  setLoading]  = useState(false);
  const [copied,   setCopied]   = useState(false);

  const draft = useMemo(() => {
    const name  = record?.contactName || record?.name || 'there';
    const deal  = record?.title || 'your project';
    const owner = window.__nx_auth?.user?.name || 'The team';
    return `Subject: Following up on ${deal}\n\nHi ${name},\n\nI wanted to follow up on our recent conversation about ${deal}. Based on what we discussed, I believe we have a strong fit for your requirements.\n\nI'd love to schedule a brief call this week to walk you through our proposal and answer any questions you may have.\n\nWould [proposed time] work for you?\n\nBest regards,\n${owner}`;
  }, [record]);

  const handleCopy = () => {
    navigator.clipboard?.writeText(draft).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRefresh = async () => {
    setLoading(true);
    try { if (typeof onRequest === 'function') await onRequest('Draft a follow-up email.'); } catch { /* noop */ }
    setTimeout(() => setLoading(false), 1200);
  };

  return (
    <AICard icon="Mail" title="Follow-up Email Draft" badge loading={loading} onRefresh={handleRefresh} refreshLabel="Re-draft">
      <pre style={{ fontSize: 11, color: 'var(--nx-text)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
        {draft}
      </pre>
      <button
        onClick={handleCopy}
        style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 10, fontSize: 11, color: copied ? 'var(--nx-success)' : 'var(--nx-accent)', background: 'transparent', border: '1px solid var(--nx-border)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
      >
        <NexusIcon name={copied ? 'Check' : 'Copy'} size={11} />
        {copied ? 'Copied!' : 'Copy email'}
      </button>
    </AICard>
  );
};
window.AIEmailDraft = AIEmailDraft;

/* ══════════════════════════════════════════════════════════════
   AI SEARCH BAR
   ══════════════════════════════════════════════════════════════ */
const AISearchBar = ({ record, entityType, onRequest, onFeedback }) => {
  const store = useStore();
  const [query,    setQuery]    = useState('');
  const [focused,  setFocused]  = useState(false);
  const inputRef = useRef(null);

  const suggestions = useMemo(() => {
    const owners = [...new Set((store.deals || []).map(d => d.owner).filter(Boolean))].slice(0, 3);
    const stages  = (store.pipeline || []).slice(0, 3).map(s => s.name);
    return [
      'Deals closing this month',
      'High priority open deals',
      owners.length ? `Deals owned by ${owners[0]}` : 'My open deals',
      stages.length ? `Deals in ${stages[0]}` : 'Stalled deals',
      'Companies with no recent activity',
      'Contacts without a linked deal',
    ];
  }, [store.deals, store.pipeline]);

  const handleSearch = (q) => {
    setQuery(q);
    if (typeof onRequest === 'function') onRequest(q);
    /* Delegate to CommandPalette if available */
    if (window.CommandPalette?.open) {
      window.CommandPalette.open(q);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--nx-surface-alt)', border: `1px solid ${focused ? 'var(--nx-accent)' : 'var(--nx-border)'}`, borderRadius: 10, padding: '8px 12px', transition: 'border-color 0.15s' }}>
        <NexusIcon name="Sparkles" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Ask AI anything about your pipeline..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={e => { if (e.key === 'Enter' && query.trim()) handleSearch(query.trim()); }}
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: 'var(--nx-text)' }}
        />
        <AIBadge />
      </div>
      {focused && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, padding: 8, zIndex: 100, boxShadow: 'var(--nx-shadow-md)' }}>
          <div style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginBottom: 6, paddingLeft: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Suggested searches</div>
          {suggestions.filter(s => !query || s.toLowerCase().includes(query.toLowerCase())).slice(0, 5).map((s, i) => (
            <button
              key={i}
              onMouseDown={() => handleSearch(s)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: 'var(--nx-text)', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--nx-surface-alt)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <NexusIcon name="Search" size={11} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
window.AISearchBar = AISearchBar;

/* ══════════════════════════════════════════════════════════════
   AI ASSISTANT PANEL — composite panel for a record
   ══════════════════════════════════════════════════════════════ */
const AIAssistantPanel = ({ record, entityType = 'deals', onRequest, onFeedback }) => {
  const [tab, setTab] = useState('overview');
  const store = useStore();
  const isDeal = entityType === 'deals';

  const TABS = [
    { id: 'overview', label: 'Overview',    icon: 'Sparkles' },
    { id: 'health',   label: 'Health',      icon: 'Activity' },
    { id: 'actions',  label: 'Actions',     icon: 'Zap' },
    ...(isDeal ? [{ id: 'email', label: 'Email', icon: 'Mail' }] : []),
  ];

  return (
    <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'linear-gradient(135deg, #f8f7ff, #eff1ff)', borderBottom: '1px solid var(--nx-border)' }}>
        <NexusIcon name="Sparkles" size={15} style={{ color: 'var(--nx-accent)' }} />
        <span style={{ fontWeight: 700, fontSize: 13 }}>AI Assistant</span>
        <AIBadge />
        <span style={{ fontSize: 10, color: 'var(--nx-text-faint)', marginLeft: 'auto' }}>Beta</span>
      </div>

      {/* Search bar */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--nx-border)' }}>
        <AISearchBar record={record} entityType={entityType} onRequest={onRequest} onFeedback={onFeedback} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            padding: '7px 4px', fontSize: 11, fontWeight: tab === t.id ? 700 : 400,
            color: tab === t.id ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
            background: 'transparent', border: 'none', cursor: 'pointer',
            borderBottom: tab === t.id ? '2px solid var(--nx-accent)' : '2px solid transparent',
            marginBottom: -1,
          }}>
            <NexusIcon name={t.icon} size={11} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tab === 'overview' && (
          <>
            {isDeal && <AIDealSummary record={record} entityType={entityType} onRequest={onRequest} onFeedback={onFeedback} />}
            {!isDeal && (
              <AICard icon="FileText" title="Record Summary" badge>
                <p style={{ fontSize: 12, color: 'var(--nx-text)', lineHeight: 1.6, margin: 0 }}>
                  {record?.name || record?.title || 'This record'} — AI summary requires the full AI module.
                  Key data points: {Object.keys(record || {}).slice(0, 5).map(k => `${k}: ${record[k]}`).join(', ')}.
                </p>
              </AICard>
            )}
            <AIMeetingSummary record={record} entityType={entityType} onRequest={onRequest} onFeedback={onFeedback} />
          </>
        )}
        {tab === 'health'   && <AIOpportunityHealth record={record} entityType={entityType} onRequest={onRequest} onFeedback={onFeedback} />}
        {tab === 'actions'  && <AIRecommendations   record={record} entityType={entityType} onRequest={onRequest} onFeedback={onFeedback} />}
        {tab === 'email'    && <AIEmailDraft        record={record} entityType={entityType} onRequest={onRequest} onFeedback={onFeedback} />}
      </div>
    </div>
  );
};
window.AIAssistantPanel = AIAssistantPanel;
